import { Button, Checkbox, MenuItem, Stack, TextField, Typography } from '@mui/material';
import dayjs, { type Dayjs } from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import ModalV2 from '/@/components/bayer/modal-v2';
import SelectionListItem from '/@/components/bayer/selection-list-item';
import { useApplicationRegisters } from '/@/hooks/application-register';
import { useCropProtectionProducts } from '/@/hooks/catalog/use-crop-protection-products';
import { useMutateCreateClaim, useMutateUpdateClaim } from '/@/hooks/claims';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Claim, ClaimProblem, ClaimType } from '/@/types/claim';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { formatDate } from '/@/utils';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { useCampaignWindow } from '../../../use-campaign-window';
import {
    CLAIM_TYPES,
    claimProblemLabel,
    claimTypeDescription,
    claimTypeLabel,
    getProblemOptions,
    getProblemStageDate,
} from '../claim-helpers';
import CreateRegistryApplication from './application';
import {
    claimInvalidFields,
    claimTypeProp,
    errorDetail,
    type FieldRegistryFlow,
    FieldRegistryFlowContext,
    flowEventProps,
    hasRelatedApplication,
    startFieldRegistryFlow,
    trackFormAbandoned,
    trackFormOpened,
    useFieldRegistryFlow,
    useFormExitTracking,
} from './flow-tracking';

/**
 * "Registrá el reclamo" — creates a claim from the add-registry modal, or edits an existing one
 * (`claim` set) from its timeline card.
 */
const ClaimForm = ({
    open,
    onClose,
    onSuccess,
    goBack,
    fieldId,
    cropMonitoring,
    claim,
}: {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    /** Back arrow to the registry type picker; absent when editing from the card. */
    goBack?: () => void;
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    claim?: Claim;
}) => {
    const isMobile = useIsMobile();
    const createClaim = useMutateCreateClaim();
    const updateClaim = useMutateUpdateClaim();
    const { typeNameById } = useCropProtectionProducts();

    const [type, setType] = useState<ClaimType | ''>(claim?.type ?? '');
    const [problem, setProblem] = useState<ClaimProblem | ''>(claim?.problem ?? '');
    const [applicationIds, setApplicationIds] = useState<number[]>(claim?.applicationRegisters ?? []);
    const [date, setDate] = useState<Dayjs | null>(claim?.date ? dayjs(claim.date) : null);
    // Once the user picks a date (or the claim already has one), a problem change no longer
    // replaces it with the matching stage's date.
    const [dateTouched, setDateTouched] = useState(Boolean(claim?.date));
    const [description, setDescription] = useState(claim?.description ?? '');
    const [addingApplication, setAddingApplication] = useState(false);
    // Errors show once the user tries to register; before that the form starts clean.
    const [showErrors, setShowErrors] = useState(false);
    // The application added from here is an attempt of its own, opened from this one.
    const [applicationFlow, setApplicationFlow] = useState<FieldRegistryFlow>();

    // Editing from the card is not a registration: only a new claim reports the funnel.
    const contextFlow = useFieldRegistryFlow();
    const flow = claim ? undefined : contextFlow;

    // Same params as the timeline's applications query, so SWR dedupes the request and a newly
    // registered application (which invalidates it) shows up here too.
    const campaignWindow = useCampaignWindow({ field: fieldId, cropMonitoring });
    const applicationsQuery = useApplicationRegisters({
        field: fieldId,
        applicationDateGte: campaignWindow.dateGte,
        applicationDateLte: campaignWindow.dateLte,
        ordering: 'application_date',
        pageSize: 10000,
    });
    const windowApplications = applicationsQuery.data?.results ?? [];
    // Linked applications outside the campaign window (an older link, or one just added with
    // another date) are listed too, so they can be seen and unselected.
    const linkedIds = [...new Set([...(claim?.applicationRegisters ?? []), ...applicationIds])];
    const linkedOutside = applicationsQuery.data
        ? linkedIds.filter((id) => !windowApplications.some((application) => application.id === id))
        : [];
    const outsideQuery = useApplicationRegisters({
        field: linkedOutside.length > 0 ? fieldId : undefined,
        ordering: 'application_date',
        pageSize: 10000,
    });
    const applications = [
        ...windowApplications,
        ...(outsideQuery.data?.results ?? []).filter((application) => linkedOutside.includes(application.id)),
    ];

    const isChemicals = type === 'CHEMICALS';
    const isMutating = createClaim.isMutating || updateClaim.isMutating;
    const invalidFields = claimInvalidFields(type, problem);
    const problemError = showErrors && Boolean(type) && invalidFields.includes('problem');

    const formTouched = Boolean(type) || Boolean(date) || description.trim() !== '' || applicationIds.length > 0;
    const { end, finish } = useFormExitTracking((exitMethod) => {
        if (flow) trackFormAbandoned(flow, 'claim', exitMethod, formTouched);
    });
    const handleCancel = () => {
        end('cancel');
        onClose();
    };
    const handleModalClose = () => {
        end('close');
        onClose();
    };

    const handleTypeChange = (nextType: ClaimType) => {
        if (nextType === type) return;
        setType(nextType);
        setProblem('');
        if (!dateTouched) setDate(null);
        if (nextType !== 'CHEMICALS') setApplicationIds([]);
    };

    const handleProblemChange = (nextProblem: ClaimProblem) => {
        setProblem(nextProblem);
        if (dateTouched) return;
        const stageDate = getProblemStageDate(nextProblem, cropMonitoring);
        setDate(stageDate ? dayjs(stageDate) : null);
    };

    const toggleApplication = (id: number) =>
        setApplicationIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

    const handleSubmit = async () => {
        if (isMutating || !fieldId) return;
        if (!type || !problem || invalidFields.length > 0) {
            setShowErrors(true);
            if (flow)
                mpTrackEvent('Field Registry Submit Attempted', {
                    ...flowEventProps(flow),
                    registry_type: 'claim',
                    invalid_fields: invalidFields,
                });
            return;
        }
        const data = {
            type,
            problem,
            description: description.trim(),
            // Left empty, the backend records the claim as of today.
            ...(date ? { date: date.format('YYYY-MM-DD') } : {}),
            applicationRegisters: isChemicals ? applicationIds : [],
        };
        try {
            if (claim) await updateClaim.trigger({ id: claim.id, ...data });
            else await createClaim.trigger({ field: fieldId, ...data });
        } catch (error) {
            if (flow)
                mpTrackEvent('Field Registry Save Failed', {
                    ...flowEventProps(flow),
                    registry_type: 'claim',
                    origin: flow.origin,
                    error_detail: errorDetail(error),
                    partial: false,
                });
            // Keep the modal (and everything typed in it) open so the user can retry.
            enqueueSnackbar(_('No se pudo guardar el reclamo. Intentá nuevamente.'), { variant: 'error' });
            return;
        }
        finish();
        if (flow)
            mpTrackEvent('Field Registry Saved', {
                ...flowEventProps(flow),
                registry_type: 'claim',
                origin: flow.origin,
                entry_source: flow.entrySource,
                claim_type: claimTypeProp(type),
                has_related_application: hasRelatedApplication(type, applicationIds),
            });
        onSuccess();
    };

    const handleAddApplication = () => {
        const started = startFieldRegistryFlow(
            {
                fieldId,
                cropMonitoring,
                origin: 'claim',
                entrySource: 'claim',
                parentFlowId: flow?.flowId,
            },
            'application'
        );
        trackFormOpened(started, 'application');
        setApplicationFlow(started);
        setAddingApplication(true);
    };

    const submitLabel = claim ? _('Guardar') : _('Registrar');

    return (
        <>
            <ModalV2
                open={open && !addingApplication}
                onClose={handleModalClose}
                goBack={
                    goBack
                        ? () => {
                              // Back to the type picker: the attempt goes on there.
                              finish();
                              goBack();
                          }
                        : undefined
                }
                title={claim ? _('Editá el reclamo') : _('Registrá el reclamo')}
                bodyProps={{ alignItems: 'stretch', textAlign: 'left' }}
                actionsProps={{ justifyContent: 'flex-end' }}
                fullScreenOnMobile
                actions={
                    <Stack
                        direction={isMobile ? 'column-reverse' : 'row'}
                        spacing={1.5}
                        sx={{ width: isMobile ? '100%' : 'auto' }}
                    >
                        <Button variant='text' color='secondary' onClick={handleCancel}>
                            {_('Cancelar')}
                        </Button>
                        {/* Enabled while invalid, so trying to register points at what's missing. */}
                        <Button variant='contained' color='secondary' disabled={isMutating} onClick={handleSubmit}>
                            {submitLabel}
                        </Button>
                    </Stack>
                }
            >
                <Stack spacing={3}>
                    <Typography variant='body1' color='primary'>
                        {_(
                            'Elegí el tipo de reclamo y el problema. Con eso, Experto te muestra los datos del lote para armar el informe'
                        )}
                    </Typography>

                    <Stack spacing={2}>
                        <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                            {_('Tipo de reclamo')}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            {CLAIM_TYPES.map((option) => (
                                <Stack key={option} flex={1}>
                                    <SelectionListItem
                                        title={claimTypeLabel(option)}
                                        subtitle={claimTypeDescription(option)}
                                        isSelected={type === option}
                                        onClick={() => handleTypeChange(option)}
                                    />
                                </Stack>
                            ))}
                        </Stack>
                        {showErrors && !type && (
                            <Typography variant='caption' color='error'>
                                {_('Elegí el tipo de reclamo.')}
                            </Typography>
                        )}
                    </Stack>

                    <Stack spacing={2}>
                        <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                            {_('Problema')}
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            value={problem}
                            disabled={!type}
                            onChange={(event) => handleProblemChange(event.target.value as ClaimProblem)}
                            SelectProps={{ displayEmpty: true }}
                            error={problemError}
                            helperText={problemError ? _('Elegí el problema.') : undefined}
                        >
                            <MenuItem value='' disabled>
                                {_('Elegir tipo')}
                            </MenuItem>
                            {getProblemOptions(type).map((option) => (
                                <MenuItem key={option} value={option}>
                                    {claimProblemLabel(option)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>

                    <Stack spacing={2}>
                        <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                            {_('Fecha de reclamo')}
                        </Typography>
                        <MuiDatePicker
                            fullWidth
                            value={date}
                            onChange={(value) => {
                                setDate(value);
                                setDateTouched(true);
                            }}
                            slotProps={{ textField: { placeholder: _('Seleccionar una fecha') } }}
                            maxDate={dayjs()}
                        />
                    </Stack>

                    {isChemicals && (
                        <Stack spacing={2}>
                            <Stack>
                                <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                    {_('Aplicación relacionada')}
                                </Typography>
                                <Typography variant='body2' color='primary'>
                                    {_('Elegí si corresponde a una de las aplicaciones registradas en el seguimiento')}
                                </Typography>
                            </Stack>
                            <Stack spacing={1}>
                                {applications.map((application) => (
                                    <Stack
                                        key={application.id}
                                        direction='row'
                                        alignItems='center'
                                        spacing={1}
                                        onClick={() => toggleApplication(application.id)}
                                        sx={{
                                            border: 1,
                                            borderColor: 'primary.mui.stroke',
                                            borderRadius: 2,
                                            p: 2,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <Stack flex={1} minWidth={0}>
                                            <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                                {typeNameById(application.type) || _('Fertilizante')}
                                            </Typography>
                                            <Typography variant='body2' color='primary'>
                                                {[
                                                    formatDate(new Date(application.applicationDate)),
                                                    application.cropProtectionProductName,
                                                    `${application.dosis} ${application.unity}`.trim(),
                                                ].join(' - ')}
                                            </Typography>
                                        </Stack>
                                        <Checkbox color='secondary' checked={applicationIds.includes(application.id)} />
                                    </Stack>
                                ))}
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                    spacing={1}
                                    sx={{
                                        bgcolor: 'primary.mui.stroke',
                                        border: 1,
                                        borderStyle: 'dashed',
                                        borderColor: 'primary.mui.stroke',
                                        borderRadius: 2,
                                        p: 2,
                                    }}
                                >
                                    <Stack flex={1}>
                                        <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                            {_('¿No está registrada?')}
                                        </Typography>
                                        <Typography variant='body2' color='primary'>
                                            {_('La agregamos en el seguimiento')}
                                        </Typography>
                                    </Stack>
                                    <Button
                                        variant='outlined'
                                        color='primary'
                                        sx={{ bgcolor: 'background.paper', flexShrink: 0 }}
                                        onClick={handleAddApplication}
                                        disabled={!fieldId}
                                    >
                                        {_('agregar una aplicación')}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Stack>
                    )}

                    <Stack spacing={2}>
                        <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                            {_('¿Qué te comentó el productor?')}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            placeholder={_('Escribir una descripción del problema')}
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                        />
                    </Stack>
                </Stack>
            </ModalV2>

            {/* Mounted only while open so each "agregar una aplicación" starts from a blank form;
                the claim form above stays mounted (just hidden), keeping what was entered. */}
            {addingApplication && (
                <FieldRegistryFlowContext.Provider value={applicationFlow}>
                    <CreateRegistryApplication
                        open={open}
                        onClose={() => setAddingApplication(false)}
                        onSuccess={(createdIds) => {
                            setAddingApplication(false);
                            // What was just added for this claim is what it relates to
                            if (createdIds?.length)
                                setApplicationIds((current) => [...new Set([...current, ...createdIds])]);
                        }}
                        fieldId={fieldId}
                    />
                </FieldRegistryFlowContext.Provider>
            )}
        </>
    );
};

export default ClaimForm;
